import axios, { AxiosResponse, AxiosError, AxiosRequestConfig, Method, ResponseType } from 'axios';

// Define interface for service call arguments
export interface TypedServiceCallArgs {
  resource: string;
  method: Method;
  data?: unknown;
  loginHeader?: {
    cbBrand: string;
    [key: string]: string;
  };
  customHeaders?: Record<string, string>;
  withCredentials?: boolean;
  responseType?: ResponseType;
  replaceUrlWithResource?: boolean;
  contentType?: string;
}

// Define interface for service response
export interface TypedServiceResponse<T> {
  data?: T | undefined;
  status?: number | undefined;
  statusText?: string | undefined;
  error?: Error | AxiosError | undefined;
  headers?: Record<string, string> | undefined;
  config?: AxiosRequestConfig | undefined;
}

/**
 * Gets a promise for a service call with proper TypeScript typing
 * @param args Service call arguments
 * @returns Promise of axios response
 */
export const getTypedServiceCallPromise = <T = unknown>({
  resource,
  method,
  loginHeader,
  data,
  customHeaders = {},
  withCredentials = true,
  responseType = 'json',
  replaceUrlWithResource,
  contentType,
}: TypedServiceCallArgs): Promise<AxiosResponse<T>> => {
  const { REACT_APP_SERVICE_URL } = window.weeksWebCloudConfig || { REACT_APP_SERVICE_URL: '/api' };

  // // Construct query parameters based on resource type
  const COMMON_QUERY_PARAM = 'consumerChannelQ=Web&memberTypeQ=WEEKS&operatorIdQ=WEB00USER';
  let customResource = resource && resource.includes('?') 
    ? `${resource}&${COMMON_QUERY_PARAM}` 
    : `${resource}?${COMMON_QUERY_PARAM}`;
  // These resource types must not have the common query params appended.
  // replaceUrlWithResource keeps the params (the caller opted into a full URL + params).
  if (
    !replaceUrlWithResource &&
    (resource.includes('weeks-offers-details') || resource.includes('mocky') || resource.includes('post-wow'))
  ) {
    customResource = resource;
  }

  // Absolute URLs must not be prefixed with the API base (avoids "https://basehttps://..." ).
  const isAbsoluteUrl = /^https?:\/\//i.test(resource);

  // Fix Error 1: Define headers as Record<string, string> to allow dynamic properties
  const headers: Record<string, string> = {
    'content-type': contentType || 'text/plain',
    ...customHeaders
  };

  if (loginHeader) {
    headers.cbBrand = loginHeader.cbBrand;
  }

  // Fix Error 2: Remove the generic type parameter from axios
  return axios({
    url: replaceUrlWithResource || isAbsoluteUrl ? customResource : `${REACT_APP_SERVICE_URL}${customResource}`,
    responseType,
    method,
    data,
    headers,
    withCredentials,
  }) as Promise<AxiosResponse<T>>; // Type assertion after the call
};

/**
 * TypeScript version of invokeService that provides proper typing
 * @param args Service call arguments
 * @returns Service response with proper TypeScript typing
 */
export const invokeTypedService = async <T = unknown>(args: TypedServiceCallArgs): Promise<TypedServiceResponse<T>> => {
  try {
    const serviceResponse = await getTypedServiceCallPromise<T>(args);
    return {
      data: serviceResponse.data,
      status: serviceResponse.status,
      statusText: serviceResponse.statusText,
      headers: serviceResponse.headers as Record<string, string>,
      config: serviceResponse.config
    };
  } catch (error) {
    // Fix Error 3: Make return type match TypedServiceResponse by ensuring all properties are optional
    return { 
      error: error as Error | AxiosError, 
      data: undefined, 
      status: (error as AxiosError)?.response?.status,
      statusText: (error as AxiosError)?.response?.statusText,
      headers: undefined,
      config: undefined
    };
  }
};

